// A return step waiting on the OMS API (2026-10-29, docs/contact-view.md "A
// step waiting on the OMS API"): the step's checklist with its title chip and
// "Check now", the spinner while Process waits for the RMA number, the
// contact's "label on its way" banner, and the $0 RMA banner with its
// "Create and send return label" box. The same components on the main
// panel, the agent / shadow panel, Other Actions and the Activity Log.

import { useEffect, useState } from 'react'
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { RMA_NUMBER_WAIT_TEXT, ZERO_DOLLAR_LABEL_CHECKBOX_TEXT, REPLACEMENT_LABEL_OPTIONAL_TEXT, zeroDollarBannerLines, formatEtTime } from '@cxhub/shared'
import { checklistLineText, finishedChipText } from './pendingLabels.js'

const STATE_COLOR = { done: 'success.main', waiting: 'warning.main', ready: 'text.secondary', failed: 'error.main', skipped: 'text.secondary' }

// row: { id, kind, status, chip, checklist }. onCheckNow(id) -> Promise.
export function PendingStepChecklist({ row, onCheckNow = null, compact = false }) {
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState(null)
  if (!row) return null
  const waiting = row.status === 'waiting'
  const chip = waiting ? row.chip : finishedChipText(row)
  const run = async () => {
    if (!onCheckNow || !row.id) return
    setChecking(true)
    setError(null)
    try { await onCheckNow(row.id) } catch (e) { setError(e?.message ?? 'Check failed') } finally { setChecking(false) }
  }
  return (
    <Box data-testid="pending-step-checklist" sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: (theme) => alpha(waiting ? theme.palette.warning.light : theme.palette.success.light, 0.18) }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {chip && <Chip size="small" color={waiting ? 'warning' : (row.status === 'done' ? 'success' : 'error')} label={chip} data-testid="pending-step-chip" />}
        {waiting && onCheckNow && row.id && (
          <Button size="small" variant="outlined" onClick={run} disabled={checking} data-testid="pending-step-check-now">
            {checking ? 'Checking…' : 'Check now'}
          </Button>
        )}
      </Stack>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0 }}>
        {(row.checklist ?? []).map((line) => (
          <Typography key={line.key} component="li" variant="caption" display="block" sx={{ color: STATE_COLOR[line.state] ?? 'text.primary', overflowWrap: 'anywhere' }} data-testid={`pending-line-${line.key}`}>
            {checklistLineText(line)}
          </Typography>
        ))}
      </Box>
      {!compact && row.lastCheckedAt && (
        <Typography variant="caption" color="text.secondary" display="block">Last checked {formatEtTime(row.lastCheckedAt)}</Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 0.5 }}>{error}</Alert>}
    </Box>
  )
}

// While Process runs on a step that creates an RMA: after a moment, the
// spinner says it is waiting for the number (up to 5 s).
export function RmaNumberWaitCaption({ delayMs = 1500 }) {
  const [show, setShow] = useState(delayMs <= 0)
  useEffect(() => {
    if (delayMs <= 0) return undefined
    const t = setTimeout(() => setShow(true), delayMs)
    return () => clearTimeout(t)
  }, [delayMs])
  if (!show) return null
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} data-testid="rma-number-wait">
      <CircularProgress size={14} />
      <Typography variant="caption" color="text.secondary">{RMA_NUMBER_WAIT_TEXT}</Typography>
    </Stack>
  )
}

// The contact's banners: this order's label waiting on another contact, and
// this contact's timed-out waits (the first line of What's next).
export function ReturnLabelBanners({ banners = [], nextSteps = [] }) {
  if (!banners.length && !nextSteps.length) return null
  return (
    <>
      {banners.map((b) => <Alert key={b} severity="info" sx={{ mb: 1 }} data-testid="label-on-its-way-banner">{b}</Alert>)}
      {nextSteps.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="pending-label-next-steps">
          <Typography variant="body2" fontWeight={700}>What&apos;s next</Typography>
          {nextSteps.map((s) => <Typography key={s} variant="body2">{s}</Typography>)}
        </Alert>
      )}
    </>
  )
}

// A $0 RMA replacement's own label step, optional (2026-10-30): unchecked
// until a person checks it; read only once the step ran.
export function ReplacementLabelOptional({ checked = false, onChange = null, readOnly = false }) {
  return (
    <FormControlLabel
      sx={{ mt: 0.25, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
      control={<Checkbox size="small" checked={Boolean(checked)} disabled={readOnly || !onChange} onChange={(e) => onChange?.(e.target.checked)} inputProps={{ 'data-testid': 'replacement-label-optional-checkbox' }} />}
      label={REPLACEMENT_LABEL_OPTIONAL_TEXT}
    />
  )
}

// The $0 RMA banner before Process, with the optional label step under it
// (unchecked by default, 2026-10-30). After Process (readOnly) it shows the
// choice that was made.
export function ZeroDollarRmaBanner({ code, what = 'refund', typical = null, checked = false, onChange = null, readOnly = false }) {
  const [line1, line2] = zeroDollarBannerLines({ code, what, typical, checked })
  return (
    <Box data-testid="zero-dollar-rma-banner" sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.warning.light, 0.25) }}>
      <Typography variant="caption" fontWeight={700} display="block">{line1}</Typography>
      <Typography variant="caption" display="block">{line2}</Typography>
      <FormControlLabel
        sx={{ mt: 0.25, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
        control={<Checkbox size="small" checked={Boolean(checked)} disabled={readOnly || !onChange} onChange={(e) => onChange?.(e.target.checked)} inputProps={{ 'data-testid': 'zero-dollar-label-checkbox' }} />}
        label={ZERO_DOLLAR_LABEL_CHECKBOX_TEXT}
      />
    </Box>
  )
}
